import React, { useState, useEffect } from 'react';
import { api } from '../api';
import { formatDate, formatMoney, todayISO } from '../dateUtils';
import { getCachedData, refreshCache } from '../store';
import { plannedTransfersNet, plannedDebtReturnsNet, normPlanned, dailyBudgetModel } from '../balance';
import PeriodForm from './PeriodForm';

export default function Periods() {
  const [periods, setPeriods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadPeriods = async () => {
    const [data, cats, accs, tx] = await Promise.all([api.getPeriods(), api.getCategories(), api.getAccounts(), api.getTransactions()]);
    setPeriods(data);
    setCategories(cats);
    setAccounts(accs);
    setTransactions(tx);
  };

  useEffect(() => {
    const cached = getCachedData();
    if (cached && Array.isArray(cached.periods)) setPeriods(cached.periods);
    if (cached && Array.isArray(cached.categories)) setCategories(cached.categories);
    if (cached && Array.isArray(cached.accounts)) setAccounts(cached.accounts);
    loadPeriods();
  }, []);

  // Обновляем список при изменениях (автосохранение периода в планировании)
  useEffect(() => {
    const onDataChanged = () => loadPeriods();
    window.addEventListener('data:changed', onDataChanged);
    return () => window.removeEventListener('data:changed', onDataChanged);
  }, []);

  // Проверка пересечений: период пересекается с другим, если их диапазоны накладываются
  const hasOverlap = (p, others) => {
    return others.some(o =>
      o.id !== p.id &&
      p.startDate <= o.endDate &&
      o.startDate <= p.endDate
    );
  };

  const overlappingIds = periods.filter(p => hasOverlap(p, periods)).map(p => p.id);

  const isCurrent = (p) => {
    const today = todayISO();
    return today >= p.startDate && today <= p.endDate;
  };

  // Период уже закончился (прошёл) — его можно только просмотреть и/или удалить
  const isPast = (p) => {
    const today = todayISO();
    return p.endDate < today;
  };

  // Открыть период: прошлые — только просмотр (readOnly), текущие/будущие — редактирование
  const openPeriod = (period) => {
    setEditingPeriod(period);
    setShowForm(true);
  };

  const periodDays = (p) => {
    const start = new Date(p.startDate);
    const end = new Date(p.endDate);
    const d = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return d > 0 ? d : 0;
  };

  const handleDelete = async (id) => {
    if (window.confirm('Удалить период? Операции останутся в истории.')) {
      await api.deletePeriod(id);
      loadPeriods();
      refreshCache();
    }
  };

  return (
    <div className="content">
      <h1>Периоды</h1>

      {showForm ? (
        <PeriodForm
          period={editingPeriod}
          categories={categories}
          accounts={accounts}
          transactions={transactions}
          readOnly={editingPeriod ? isPast(editingPeriod) : false}
          onSave={async (data) => {
            if (editingPeriod) {
              await api.updatePeriod(editingPeriod.id, data);
            } else {
              await api.createPeriod(data);
            }
            setShowForm(false);
            setEditingPeriod(null);
            loadPeriods();
            refreshCache();
          }}
          onCancel={() => { setShowForm(false); setEditingPeriod(null); }}
        />
      ) : periods.length === 0 ? (
        <div className="card" style={{ textAlign: 'center' }}>
          <div className="card-title">Ничего не запланировано</div>
          <p style={{ margin: 0 }}>Создайте период, чтобы планировать доходы и расходы.</p>
          <button onClick={() => { setEditingPeriod(null); setShowForm(true); }}>Запланировать период</button>
        </div>
      ) : (
        <>
          <button onClick={() => { setEditingPeriod(null); setShowForm(true); }}>+ Запланировать период</button>

          {overlappingIds.length > 0 && (
            <div className="card" style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)' }}>
              <div style={{ fontWeight: 700, color: 'var(--danger)', marginBottom: 4 }}>⚠️ Пересечение периодов</div>
              <p style={{ margin: 0, color: 'var(--text-dim)' }}>
                Даты некоторых периодов пересекаются друг с другом. Они подсвечены красным — проверьте и исправьте даты, чтобы периоды не накладывались.
              </p>
            </div>
          )}

          <ul className="list">
            {/* Сверху — самый новый период, внизу — самый старый */}
            {[...periods].sort((a, b) => String(b.startDate || '').localeCompare(String(a.startDate || ''))).map(period => {
              const overlap = hasOverlap(period, periods);
              const current = isCurrent(period);
              const past = isPast(period);
              const days = periodDays(period);
              // Фиксированный базовый дневной лимит: (стартовый остаток + полный план) ÷ все дни.
              // Тот же, что на главной и в самой форме планирования, — считается единой моделью.
              const model = dailyBudgetModel(period, categories, accounts, transactions, period.endDate);
              const daily = model ? Math.round(model.baseDailyLimit * 100) / 100 : 0;
              return (
                <li
                  key={period.id}
                  className={`list-item${overlap ? ' period-overlap' : ''}${current ? ' period-current' : ''}${past ? ' period-past' : ''}`}
                  onClick={() => openPeriod(period)}
                  title={past ? 'Просмотреть период' : 'Открыть период'}
                >
                  <div className="list-item-left">
                    <span className="cat-icon">📅</span>
                    <div>
                      <div className="list-item-title">{formatDate(period.startDate)} — {formatDate(period.endDate)}</div>
                      <div className="list-item-sub">
                        Ежедневный остаток: {formatMoney(daily)}/день
                        {past && <span style={{ color: 'var(--text-dim)' }}> · Прошёл</span>}
                        {current && <span style={{ color: 'var(--primary)' }}> · Сейчас</span>}
                        {overlap && <span style={{ color: 'var(--danger)' }}> · Пересечение!</span>}
                      </div>
                    </div>
                  </div>
                  <div className="actions">
                    <button className="small-icon danger" onClick={(e) => { e.stopPropagation(); handleDelete(period.id); }} title="Удалить">🗑️</button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
